/**
 * Notification Context
 * Manages push notification registration and foreground message handling
 * Registers FCM token after customer login, listens for incoming messages
 */

import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from './AuthContext';
import { useToast } from './ToastContext';
import { messaging, onMessage } from '../config/firebase';
import { notificationService } from '../services/notificationService';

const NotificationContext = createContext(undefined);

export function NotificationProvider({ children }) {
  const { isAuthenticated } = useAuth();
  const { showToast } = useToast();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const tokenRegisteredRef = useRef(false);

  /**
   * Register FCM token when customer authenticates
   */
  useEffect(() => {
    if (!isAuthenticated || tokenRegisteredRef.current) return;

    const registerToken = async () => {
      const token = await notificationService.registerForPushNotifications();
      if (token) {
        tokenRegisteredRef.current = true;
      }
    };

    registerToken();
  }, [isAuthenticated]);

  /**
   * Reset registration flag on logout
   */
  useEffect(() => {
    if (!isAuthenticated) {
      tokenRegisteredRef.current = false;
    }
  }, [isAuthenticated]);

  /**
   * Listen for foreground messages
   */
  useEffect(() => {
    if (!isAuthenticated) return;

    const unsubscribe = onMessage(messaging, (payload) => {
      const title = payload.notification?.title || 'New Notification';
      const body = payload.notification?.body || '';
      showToast(body, 'success', null, title);
      fetchNotifications();
    });

    return () => unsubscribe();
  }, [isAuthenticated, showToast]);

  /**
   * Fetch notifications from API
   */
  const fetchNotifications = useCallback(async () => {
    if (!isAuthenticated) return;
    try {
      const data = await notificationService.getNotifications();
      setNotifications(data);
      setUnreadCount(data.filter((n) => !n.isRead).length);
    } catch {
      // Silently fail — notifications are non-critical
    }
  }, [isAuthenticated]);

  /**
   * Load notifications on auth change
   */
  useEffect(() => {
    if (isAuthenticated) {
      fetchNotifications();
    } else {
      setNotifications([]);
      setUnreadCount(0);
    }
  }, [isAuthenticated, fetchNotifications]);

  /**
   * Mark a single notification as read
   */
  const markAsRead = useCallback(async (notificationId) => {
    try {
      await notificationService.markAsRead(notificationId);
      setNotifications((prev) =>
        prev.map((n) => (n._id === notificationId ? { ...n, isRead: true } : n))
      );
      setUnreadCount((prev) => Math.max(0, prev - 1));
    } catch {
      // Silently fail
    }
  }, []);

  /**
   * Mark all notifications as read
   */
  const markAllAsRead = useCallback(async () => {
    try {
      await notificationService.markAllAsRead();
      setNotifications((prev) => prev.map((n) => ({ ...n, isRead: true })));
      setUnreadCount(0);
    } catch {
      // Silently fail
    }
  }, []);

  const value = {
    notifications,
    unreadCount,
    fetchNotifications,
    markAsRead,
    markAllAsRead,
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
}

/**
 * Custom hook to use notification context
 */
export function useNotifications() {
  const context = useContext(NotificationContext);
  if (context === undefined) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
}

export default NotificationContext;
